/**
 * OrganizationSection — Ayarlar > Organizasyon (super_admin only)
 *
 * Tenant-wide config: workspace adı + varsayılan timezone.
 */
import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";
import { useTranslation } from "react-i18next";
import { TEST_IDS } from "../../constants/testIds";
import { COMMON_TIMEZONES } from "../../lib/dates";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Building2 } from "lucide-react";

export default function OrganizationSection() {
  const { t } = useTranslation();
  const { tenant, setTenant } = useAuthStore();
  const [name, setName] = useState(tenant?.name || "");
  const [tz, setTz] = useState(tenant?.timezone || "UTC");
  const [saving, setSaving] = useState(false);

  const dirty = name !== (tenant?.name || "") || tz !== (tenant?.timezone || "UTC");

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/tenant", { name, timezone: tz });
      setTenant(data);
      toast.success(t("settings.saved"));
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Kaydedilemedi",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="max-w-3xl space-y-5 rounded-lg border border-zinc-200 bg-white p-6"
      data-testid="organization-section"
    >
      <div className="flex items-center gap-2 border-b border-zinc-100 pb-3">
        <Building2 className="h-4 w-4 text-zinc-500" strokeWidth={2} />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-700">
          Organizasyon
        </h2>
      </div>
      <div className="flex items-start gap-3 rounded-md bg-zinc-50 p-3 text-xs text-zinc-600">
        <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" strokeWidth={2} />
        <div>
          <div className="font-semibold text-zinc-800">Organizasyon ayarları</div>
          <div className="mt-0.5">
            Bu bilgiler tenantın (şirketin) tüm kullanıcılarını etkiler. Yalnızca
            Süper Yönetici düzenleyebilir.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">
            {t("settings.tenant_name")}
          </Label>
          <Input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid={TEST_IDS.tenantNameInput}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">
            {t("settings.timezone")}
          </Label>
          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger
              className="mt-1"
              data-testid={TEST_IDS.tenantTimezoneSelect}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMMON_TIMEZONES.map((z) => (
                <SelectItem key={z} value={z}>
                  {z}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end border-t border-zinc-100 pt-4">
        <Button
          type="submit"
          disabled={saving || !dirty}
          data-testid={TEST_IDS.tenantSaveBtn}
          className="bg-zinc-900 text-white hover:bg-zinc-800"
        >
          {saving ? "Kaydediliyor..." : t("projects.save")}
        </Button>
      </div>
    </form>
  );
}
